import { useLanguage } from "@/contexts/LanguageContext";

type Language = 'es' | 'en' | 'sv';

/**
 * Slug mappings: maps a "route key" (English-based) to the localized slug per language.
 * The route key is what's used in code; the slug is what appears in the URL.
 */
const slugMap: Record<string, Record<Language, string>> = {
  '/': { es: '/', en: '/en', sv: '/sv' },
  '/services': { es: '/servicios', en: '/en/services', sv: '/sv/tjanster' },
  '/faq': { es: '/preguntas-frecuentes', en: '/en/faq', sv: '/sv/vanliga-fragor' },
  '/blog': { es: '/blog', en: '/en/blog', sv: '/sv/blogg' },
  '/contact': { es: '/contacto', en: '/en/contact', sv: '/sv/kontakt' },
  '/about': { es: '/sobre-mi', en: '/en/about', sv: '/sv/om-mig' },
  '/squeeze': { es: '/consulta', en: '/en/squeeze', sv: '/sv/konsultation' },
  '/squeeze/hair-loss': { es: '/consulta/caida-cabello', en: '/en/squeeze/hair-loss', sv: '/sv/konsultation/harfall' },
  '/squeeze/acne': { es: '/consulta/acne', en: '/en/squeeze/acne', sv: '/sv/konsultation/akne' },
  '/squeeze/moles': { es: '/consulta/lunares', en: '/en/squeeze/moles', sv: '/sv/konsultation/leverfackar' },
  '/squeeze/laser': { es: '/consulta/laser', en: '/en/squeeze/laser', sv: '/sv/konsultation/laser' },
  '/testimonials': { es: '/testimonios', en: '/en/testimonials', sv: '/sv/omdomen' },
  '/booking': { es: '/reservar', en: '/en/booking', sv: '/sv/boka' },
  '/videos': { es: '/videos', en: '/en/videos', sv: '/sv/videor' },
  '/aquapure': { es: '/aquapure', en: '/en/aquapure', sv: '/sv/aquapure' },
  '/aesthetic-treatments': { es: '/tratamientos-esteticos', en: '/en/aesthetic-treatments', sv: '/sv/estetiska-behandlingar' },
  '/co2-laser': { es: '/laser-co2', en: '/en/co2-laser', sv: '/sv/co2-laser' },
  '/botox-barcelona': { es: '/botox-barcelona', en: '/en/botox-barcelona', sv: '/sv/botox-barcelona' },
  '/prp-barcelona': { es: '/prp-barcelona', en: '/en/prp-barcelona', sv: '/sv/prp-barcelona' },
  '/acne-treatment-barcelona': { es: '/tratamiento-acne-barcelona', en: '/en/acne-treatment-barcelona', sv: '/sv/aknebehandling-barcelona' },
  '/hair-loss-treatment-barcelona': { es: '/tratamiento-capilar-barcelona', en: '/en/hair-loss-treatment-barcelona', sv: '/sv/harfallsbehandling-barcelona' },
  '/anti-aging-barcelona': { es: '/anti-envejecimiento-barcelona', en: '/en/anti-aging-barcelona', sv: '/sv/anti-aging-barcelona' },
  '/tarifas': { es: '/tarifas', en: '/en/pricing', sv: '/sv/priser' },
  '/pricing': { es: '/tarifas', en: '/en/pricing', sv: '/sv/priser' },
  '/dermatologist-barcelona': { es: '/dermatologo-barcelona', en: '/en/dermatologist-barcelona', sv: '/sv/hudlakare-barcelona' },
  '/mole-check-barcelona': { es: '/revision-lunares-barcelona', en: '/en/mole-check-barcelona', sv: '/sv/leverfackskontroll-barcelona' },
  '/skin-cancer-screening-barcelona': { es: '/deteccion-cancer-piel-barcelona', en: '/en/skin-cancer-screening-barcelona', sv: '/sv/hudcancerscreening-barcelona' },
  '/melanoma-specialist-barcelona': { es: '/especialista-melanoma-barcelona', en: '/en/melanoma-specialist-barcelona', sv: '/sv/melanomspecialist-barcelona' },
  '/ads/hair-loss-treatment-barcelona': { es: '/ads/tratamiento-capilar-barcelona', en: '/en/ads/hair-loss-treatment-barcelona', sv: '/sv/ads/harfallsbehandling-barcelona' },
  '/ads/acne-treatment-barcelona': { es: '/ads/tratamiento-acne-barcelona', en: '/en/ads/acne-treatment-barcelona', sv: '/sv/ads/aknebehandling-barcelona' },
  '/rosacea-treatment-barcelona': { es: '/tratamiento-rosacea-barcelona', en: '/en/rosacea-treatment-barcelona', sv: '/sv/rosaceabehandling-barcelona' },
  '/botox-barcelona-treatment': { es: '/botox-barcelona-tratamiento', en: '/en/botox-barcelona-treatment', sv: '/sv/botox-barcelona-behandling' },
  '/english-speaking-dermatologist-barcelona': { es: '/dermatologo-ingles-barcelona', en: '/en/english-speaking-dermatologist-barcelona', sv: '/sv/engelsktalande-hudlakare-barcelona' },
  '/gracias': { es: '/gracias', en: '/en/thank-you', sv: '/sv/tack' },
  '/thank-you': { es: '/gracias', en: '/en/thank-you', sv: '/sv/tack' },
};

/**
 * Get a localized path for the given route key and language.
 * Handles blog article paths dynamically.
 */
export function getLocalizedPath(routeKey: string, lang: Language): string {
  // Handle blog article paths: /blog/some-slug
  if (routeKey.startsWith('/blog/') && routeKey !== '/blog') {
    const slug = routeKey.slice(6); // remove '/blog/'
    if (lang === 'es') return `/blog/${slug}`;
    if (lang === 'sv') return `/sv/blogg/${slug}`;
    return `/en/blog/${slug}`;
  }

  // Handle hash anchors (e.g., /#about)
  if (routeKey.includes('#')) {
    const [path, hash] = routeKey.split('#');
    const localizedBase = slugMap[path]?.[lang] || path;
    const prefix = lang === 'es' ? '' : `/${lang}`;
    if (path === '/' || path === '') {
      return `${prefix || '/'}#${hash}`;
    }
    return `${localizedBase}#${hash}`;
  }

  // Direct lookup
  if (slugMap[routeKey]) {
    return slugMap[routeKey][lang];
  }

  // Fallback: just add prefix
  const prefix = lang === 'es' ? '' : `/${lang}`;
  return `${prefix}${routeKey}`;
}

/**
 * Hook that returns a path localizer function based on current language.
 */
export function useLocalizedPath() {
  const { language } = useLanguage();
  
  const lp = (routeKey: string): string => {
    return getLocalizedPath(routeKey, language as Language);
  };

  return { lp, language };
}

/**
 * Reverse lookup: given a localized path, find the route key.
 * Used by LanguageSwitcher to map between languages.
 */
export function getRouteKeyFromPath(path: string): { routeKey: string; lang: Language } | null {
  // Check blog paths first
  const blogMatch = path.match(/^\/(en\/blog|sv\/blogg|blog)\/(.+)$/);
  if (blogMatch) {
    const slug = blogMatch[2];
    if (path.startsWith('/en/')) return { routeKey: `/blog/${slug}`, lang: 'en' };
    if (path.startsWith('/sv/')) return { routeKey: `/blog/${slug}`, lang: 'sv' };
    return { routeKey: `/blog/${slug}`, lang: 'es' };
  }

  // Search through slugMap
  for (const [routeKey, langs] of Object.entries(slugMap)) {
    for (const [lang, localizedPath] of Object.entries(langs)) {
      if (localizedPath === path) {
        return { routeKey, lang: lang as Language };
      }
    }
  }

  return null;
}

export { slugMap };
